import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/data/content";

/**
 * Shared shell for every dashboard sub-page (Vault, Builder, Project Detail).
 * Renders the brand header + a back link + signs the user out cleanly.
 */
export default function DashboardShell({ children, back = "/dashboard", backLabel = "Dashboard" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div data-testid="dashboard-shell" className="min-h-screen bg-[#F7F5EF] text-neutral-900">
      <header className="border-b border-neutral-200/70">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2 sm:gap-3" data-testid="shell-logo">
            <span className="font-serif text-lg sm:text-2xl tracking-tight text-neutral-900">
              {BRAND.wordmark.lead}
            </span>
            <span className="font-serif italic text-lg sm:text-2xl text-gold">
              {BRAND.wordmark.tail}
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <span
              className="hidden sm:inline text-xs uppercase tracking-[0.18em] text-neutral-500"
              data-testid="shell-user-email"
            >
              {user?.email}
            </span>
            <button
              onClick={onLogout}
              data-testid="shell-logout"
              className="inline-flex items-center gap-2 border border-neutral-200 hover:border-gold hover:text-gold px-3 py-2 text-xs uppercase tracking-[0.18em] transition"
            >
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 pt-8 sm:pt-10">
        <Link
          to={back}
          data-testid="shell-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-gold transition"
        >
          <ArrowLeft size={12} /> {backLabel}
        </Link>
      </div>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-14">
        {children}
      </main>
    </div>
  );
}
